import React, { Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FormattedMessage, useIntl } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import InstructionalText from '../notifications/InstructionalText';
import PageHeader from '@components/ssap/headers/PageHeader';
import SourceSummary from './pageIncludes/summary/SourceSummary';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';
import UniversalInput from '@lib/wrappers/UniversalInput';

// ActionCreators

import { changeData, clearMethods, setMethods } from '@actions/ssap/ssapActionCreators';
import { showModal } from '@actions/common/actionCreators';

// Hooks

import { useIfMounted } from '@hooks/useIfMounted';
import { useInternationalizedOptions } from '@hooks/useInternationalizedOptions';

// Utils

import { getArrayOfObjectsToPrimitive } from '@utils/miscellaneous/getArrayOfObjectsToPrimitive';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '@utils/tools/preventerTools';
import { internationalizeList } from '@utils/miscellaneous/internationalizeList';

// Constants

import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { ssapModalTypes as MODAL_TYPES } from '@constants/ssap/modals/ssapModalTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import EXTERNAL_LINKS from '@constants/configs/externalEndpoints';




const Page3_34_E1 = props => {

    const {
        data,
        form,
        formName,
        householdMembers,
        methods,
        page,
        environment: {
            isState ,
            language
        }
    } = props;

    const {
        isFormOnTopLevelValid,
        areAllInputsValid,
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const {
        buttonContainer: ButtonContainer
    } = props;

    const {
        hhmIndex : member,
        currentHhm
    } = props;

    const {
        expenses,
        hhmIncomeParams: {
            hasAnyDeduction
        },
        name
    } = currentHhm;

    const intlApi = useIntl();

    const isMounted = useIfMounted();

    useEffect(() => {
        if(isMounted) {
            methods[formName]
                .onFormSubmit(
                    page,
                    null,
                    null,
                    data
                );
        }
    }, [ getArrayOfObjectsToPrimitive(expenses) ]);

    const handleDeductionSourceDeletion = (member, deductionIndex) => {

        const newState = { ...data };

        newState
            .taxHousehold
            .householdMember[member]
            .expenses
            .splice(deductionIndex, 1);

        const isNoDeduction = newState
            .taxHousehold
            .householdMember[member]
            .expenses
            .length === 0;

        if(isNoDeduction) {
            newState
                .taxHousehold
                .householdMember[member]
                .hhmIncomeParams
                .hasAnyDeduction = null;
        }

        props.actions.changeData(newState);
    };

    const handleDeductionSourceEditing = deductionIndex => {
        props.actions.showModal({
            modalType: MODAL_TYPES.ADD_INCOME_OR_DEDUCTION_SOURCE_MODAL,
            modalData: {
                source: expenses[deductionIndex],
                index: deductionIndex,
                parameter: 'expenses',
                pageToReturn: page
            }
        });
    };

    const handleAddDeductionSourceClick = () => {
        props.actions.showModal({
            modalType: MODAL_TYPES.ADD_INCOME_OR_DEDUCTION_SOURCE_MODAL,
            modalData: {
                source: {},
                index: null,
                parameter: 'expenses',
                pageToReturn: page
            }
        });
    };

    useEffect(() => {
        props.actions.setMethods('Page3_34_E1',
            {
                handleDeductionSourceDeletion,
                handleDeductionSourceEditing,
                handleAddDeductionSourceClick
            });

        return () => {
            props.actions.clearMethods('Page3_34_E1');
        };
    }, [ expenses.map(expense => Object.values(expense).toLocaleString()).toLocaleString() ]);

    const onFieldChange = (...args) => {
        const [ newData, index ] = args;

        const newState = { ...data };

        newState.taxHousehold.householdMember[index] = newData;

        props.actions.changeData(newState);
    };

    const checkIfDisabled = () => {
        return expenses.length > 0 ? false : !hasAnyDeduction;
    };

    const checkTopLevelValidation = () => {
        switch(true) {
            case hasAnyDeduction === true:
                return expenses.length > 0;
            case hasAnyDeduction === false:
                return true;
            case hasAnyDeduction === null:
                return false;
        }
    };

    const getLis = () => {

        const ids = [
            'ssap.input.option.alimony_paid',
            'ssap.input.option.student_loan_interest',
            isState.ID
                ? 'ssap.input.option.other_eligible_deductions'
                : 'ssap.input.option.other_deductions'
        ];

        return internationalizeList(ids, 'li');
    };
    
    const dropdownSourcesList = useInternationalizedOptions([ ...DROPDOWN_OPTIONS.DEDUCTION_SOURCES ]);
    const dropdownFrequencyList = useInternationalizedOptions([ ...DROPDOWN_OPTIONS.FREQUENCY ]);

    const anchor = intlApi.formatMessage({ id: 'common.here' });

    const link =
        `<a
            href = ${ EXTERNAL_LINKS.GENERAL.HEALTH_CARE_GOV_REPORTING_DEDUCTIONS[language] }
            class = 'usa-link--external'
            target = '_blank'
            rel = 'noopener noreferrer'
        >
            ${ anchor }
        </a>`;

    return (
        <Fragment>
            <div id = 'iiDeductions' >
                <div className = 'subsection' >
                    <PageHeader
                        text = 'ssap.subheader.deductions_for_person'
                        textReplacements = { {
                            personName: normalizeNameOfPerson(name)
                        } }
                        headerClassName = 'gi-heading-alt'
                    />
                    <FormattedMessage
                        id = 'ssap.income_information.deductions.notification'
                        tagName = 'p'
                    />

                    <Form
                        topLevelValidity = { checkTopLevelValidation() }
                        { ...{ formName } }
                    >

                        {
                            expenses.length === 0
                                ? (
                                    <div>
                                        <ValidationErrorMessage
                                            isVisible = { isFormSubmitted && !isFormOnTopLevelValid && areAllInputsValid }
                                            messageText = { VALIDATION_ERROR_MESSAGES.DEDUCTION_SOURCE }
                                            errorClasses = { [ 'margin-l-0' ] }
                                        />

                                        <UniversalInput
                                            legend = { {
                                                legendText: 'ssap.input.legend.non_internationalized_text',
                                                legendTextReplacements: {
                                                    text: (
                                                        <div>
                                                            <InstructionalText
                                                                Component = {
                                                                    <p data-instructions = 'instructional_text.deductions' >
                                                                        { 'ssap.income_information.deductions.pays_deductions' }
                                                                    </p>
                                                                }
                                                                replacements = { {
                                                                    personName: normalizeNameOfPerson(name),
                                                                    link
                                                                } }
                                                                childrenReplacements = { {
                                                                    personName: normalizeNameOfPerson(name)
                                                                } }
                                                                required
                                                            />
                                                            <ul className = 'usa-list gi-list--ul' >
                                                                { getLis() }
                                                            </ul>
                                                        </div>
                                                    )
                                                }
                                            } }
                                            label = { {
                                                labelClass: [ 'margin-t-0' ],
                                                showRequiredIcon: false
                                            } }
                                            inputData = { {
                                                isInputRequired: true,
                                                type: INPUT_TYPES.RADIO_BUTTONS,
                                                name: 'hasAnyDeduction',
                                                value: hasAnyDeduction,
                                                options: DROPDOWN_OPTIONS.YES_NO,
                                                validationType: VALIDATION_TYPES.CHOSEN,
                                                currentData: currentHhm,
                                                fields: [ 'hhmIncomeParams' ]
                                            } }
                                            errorClasses = { [ 'margin-l-0' ] }
                                            inputActions = { {
                                                onFieldChange
                                            } }
                                        />
                                    </div>
                                )
                                : (
                                    <div className = 'grid-col-fill margin-tb-20' >
                                        <FormattedMessage
                                            id = 'ssap.income_information.deductions.add_deductions'
                                            tagName = 'p'
                                        />
                                        <SourceSummary
                                            buttonContainer = { ButtonContainer }
                                            sources = { expenses }
                                            member = { member }
                                            dropdownSourcesList = { dropdownSourcesList }
                                            dropdownFrequencyList = { dropdownFrequencyList }
                                            summaryType = 'common.deduction'
                                        />
                                    </div>
                                )
                        }
                    </Form>

                    <ButtonContainer
                        customButtonClassName = { checkIfDisabled() ? 'btn-disabled' : '' }
                        type = 'addDeductionSourceButton'
                        args = { [ householdMembers, member ] }
                        isDisabled = { checkIfDisabled() }
                    />

                </div>
            </div>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    data: state.ssap.data,
    form: state.ssap.form,
    environment: state.common.environment,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember,

    methods: state.ssap.methods,
    page: state.ssap.page
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        clearMethods,
        showModal,
        setMethods
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page3_34_E1);
